import { FC, useMemo, useState, useEffect, useCallback } from 'react'
import styles from 'components/features/external/PublicSpendingTrend/LineChart.styl'
import { calcChart } from 'components/features/external/PublicSpendingTrend/helpers'
import { LineChartPath } from 'components/features/external/PublicSpendingTrend/LineChartPath'
import { LineChartPoints } from 'components/features/external/PublicSpendingTrend/LineChartPoints'
import { LineChartHover } from 'components/features/external/PublicSpendingTrend/LineChartHover'
import { LineChartAxis } from 'components/features/external/PublicSpendingTrend/LineChartAxis'
import { PublicTrendItem } from 'interfaces/publicDashboard'
import { LineChartSections } from 'components/features/external/PublicSpendingTrend/LineChartSections'
import { LineChartInteractionHandler } from 'components/features/pfm/LineChartInteractionHandler/LineChartInteractionHandler'
import { SizeHandler } from 'components/features/pfm/SizeHandler/SizeHandler'

type LineChartProps = {
  items: PublicTrendItem[]
  isPreliminary?: boolean
  onHighlight: (item: PublicTrendItem, index: number) => void
}

export const LineChart: FC<LineChartProps> = (props) => {
  const { items, onHighlight, isPreliminary } = props

  const [boundRect, setBoundRect] = useState<ClientRect | null>(null)
  const {
    width = null,
    height = null,
  } = boundRect || {}
  const handleChange = useCallback<(rect: ClientRect) => void>((rect) => {
    setBoundRect(rect)
  }, [])

  const lastIndex = items.length - 1
  const [highlightIndex, setHighlightIndex] = useState(lastIndex)

  const handleMouseLeave = useCallback(() => {
    setHighlightIndex(lastIndex)
  }, [lastIndex])

  useEffect(() => {
    onHighlight(items[highlightIndex], highlightIndex)
  }, [items, highlightIndex, onHighlight])

  const handleIndexChange = useCallback<(index: number) => void>((index) => {
    if (index > lastIndex) {
      setHighlightIndex(lastIndex)
      return
    }

    setHighlightIndex(index)
  }, [lastIndex])

  const chartChild = useMemo(() => {
    const sizeProbeChild = (
      <div>
        <SizeHandler onChange={ handleChange }>
          <div className={ styles.chart } />
          <br />
        </SizeHandler>
        <div className={ styles.sections } />
      </div>
    )

    if (!width || !height || !items || items.length === 0) {
      return sizeProbeChild
    }

    const { points, zeroAbscissa } = calcChart(items, width, height)

    if (!points || points.length === 0) {
      return sizeProbeChild
    }

    return (
      <LineChartInteractionHandler
        count={ points.length }
        width={ width }
        onChange={ handleIndexChange }
      >
        <SizeHandler onChange={ handleChange }>
          <svg className={ styles.chart }>
            <LineChartAxis
              points={ points }
              zero={ zeroAbscissa }
              width={ width }
            />
            <LineChartPath
              points={ points }
              zero={ zeroAbscissa }
            />
            <LineChartHover
              points={ points }
              index={ highlightIndex }
              height={ height }
              width={ width }
            />
            <LineChartPoints
              points={ points }
            />
          </svg>
        </SizeHandler>
        <div className={ styles.sections }>
          <LineChartSections
            items={ items }
            index={ highlightIndex }
            width={ width }
            isPreliminary={ isPreliminary }
          />
        </div>
      </LineChartInteractionHandler>
    )
  }, [handleIndexChange, height, highlightIndex, isPreliminary, items, width, handleChange])

  return (
    <div
      className={ styles.LineChart }
      onMouseLeave={ handleMouseLeave }
    >
      { chartChild }
    </div>
  )
}
